import { createContext, useContext, useCallback, useMemo, useRef, useState, useEffect } from 'react';

// ── Unsaved-work registry ──
// A single place any in-progress form (expense draft, leave request, travel
// request, admin forms) can register itself as "dirty" so the app can:
//   1. Warn on refresh/close/navigate-away via the native beforeunload prompt.
//   2. Let the "update available" banner (see AppStatusBanner.jsx) show the
//      user exactly what they'd lose before forcing a reload for a new deploy.
//
// Items are keyed by an arbitrary id (e.g. `expense-${reportId||'new'}`).
// Only dirty===true items count toward "has unsaved work" — a registered but
// clean form is invisible to beforeunload and the reload-confirmation modal.

const UnsavedWorkContext = createContext(null);

export function UnsavedWorkProvider({ children }) {
  // Map id -> { label, href, dirty }. Held in a ref so register/update from
  // deep in the tree don't need to fight React batching; a version counter
  // triggers re-renders for consumers that need the live list (the modal).
  const itemsRef = useRef(new Map());
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const register = useCallback((id, entry) => {
    itemsRef.current.set(id, { label: '', href: '', dirty: false, ...entry });
    bump();
  }, [bump]);

  const update = useCallback((id, patch) => {
    const existing = itemsRef.current.get(id);
    if (!existing) return;
    itemsRef.current.set(id, { ...existing, ...patch });
    bump();
  }, [bump]);

  const unregister = useCallback((id) => {
    if (itemsRef.current.delete(id)) bump();
  }, [bump]);

  const getDirtyItems = useCallback(() => {
    return Array.from(itemsRef.current.entries())
      .filter(([, v]) => v.dirty === true)
      .map(([id, v]) => ({ id, ...v }));
  }, []);

  // Reload-confirmation modal state, shared by the update banner and any
  // other caller (e.g. a nav link that wants to warn before leaving).
  const [reloadModalOpen, setReloadModalOpen] = useState(false);

  const requestReload = useCallback(() => {
    if (getDirtyItems().length > 0) {
      setReloadModalOpen(true);
    } else {
      window.location.reload();
    }
  }, [getDirtyItems]);

  const closeReloadModal = useCallback(() => setReloadModalOpen(false), []);
  const forceReload = useCallback(() => window.location.reload(), []);

  // Single beforeunload listener for the whole app.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (getDirtyItems().length === 0) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [getDirtyItems]);

  const value = useMemo(() => ({
    register,
    update,
    unregister,
    getDirtyItems,
    requestReload,
    reloadModalOpen,
    closeReloadModal,
    forceReload,
  }), [register, update, unregister, getDirtyItems, requestReload, reloadModalOpen, closeReloadModal, forceReload]);

  return (
    <UnsavedWorkContext.Provider value={value}>
      {children}
    </UnsavedWorkContext.Provider>
  );
}

export function useUnsavedWork() {
  const ctx = useContext(UnsavedWorkContext);
  if (!ctx) throw new Error('useUnsavedWork must be used within UnsavedWorkProvider');
  return ctx;
}

// Registers one editable form's unsaved state. Call with a stable `id`;
// label/href/dirty can change across renders and the registry entry is
// kept in sync. Unregisters automatically on unmount (e.g. navigating away
// after a successful save, or the component tearing down).
export function useUnsavedGuard({ id, label, href, dirty }) {
  const { register, update, unregister } = useUnsavedWork();
  const registeredRef = useRef(false);

  useEffect(() => {
    register(id, { label, href, dirty });
    registeredRef.current = true;
    return () => {
      unregister(id);
      registeredRef.current = false;
    };
    // Only re-register if the id itself changes (e.g. reportId materializes
    // from 'new' to a real id) — label/href/dirty updates are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!registeredRef.current) return;
    update(id, { label, href, dirty });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, label, href, dirty]);
}
